import {getUrlParameter} from 'libs/url/url';

import {BANNER_CAMPAIGN, DEFAULT_CAMPAIGN, ONE_DAY_IN_MILLISECONDS} from '../constants/constants';
import {BannerContentType} from '../types';

/**
 * Parses the json only when it's absent in a cache
 */
export const parseWithCache = (() => {
    const cache: Record<string, BannerContentType[]> = {};

    return (json: string) => {
        if (cache[json]) {
            return cache[json];
        }

        cache[json] = JSON.parse(json);
        return cache[json];
    };
})();

const getDefaultBanner = (data: BannerContentType[]) =>
    data.find(({campaign: id}) => id === (DEFAULT_CAMPAIGN as string)) || null;

/**
 * Campaign cookies helper
 */
export const campaignCookies = {
    /**
     * Gets the campaign value from cookies
     */
    get(cookies?: Record<string, string>): string {
        return cookies?.[BANNER_CAMPAIGN] || '';
    },

    /**
     * Saves the campaign value in cookies
     */
    set({
        content,
        savedCampaign,
        urlCampaign,
    }: {
        content: Maybe<BannerContentType>;
        savedCampaign: string;
        urlCampaign: string;
    }) {
        if (
            !__NODEJS__ &&
            urlCampaign &&
            // we save the campaign from the URL to the cookies only when it differs from the one already saved
            !(savedCampaign === urlCampaign) &&
            content?.campaign !== DEFAULT_CAMPAIGN &&
            Number(content?.campaign_time) > 0
        ) {
            const expires = new Date(
                Date.now() + ONE_DAY_IN_MILLISECONDS * Number(content?.campaign_time),
            ).toUTCString();

            document.cookie = `${BANNER_CAMPAIGN}=${content?.campaign};expires=${expires}`;
        }
    },
};

/**
 * Gets the banner content from the response
 */
export const getBannerContent = (
    json: string,
    search: string,
    cookies?: Record<string, string>,
): Maybe<BannerContentType> => {
    try {
        const urlCampaign = getUrlParameter('campaign', search);
        const savedCampaign = campaignCookies.get(cookies);
        const data = parseWithCache(json);
        const content =
            data.find(({campaign: id}) => id === (urlCampaign || savedCampaign)) ||
            getDefaultBanner(data);

        campaignCookies.set({content, savedCampaign, urlCampaign});

        return content;
    } catch {
        return null;
    }
};

/**
 * Get banner image
 */
export const getBannerImage = (bannerData: BannerContentType | null) => {
    return bannerData?.image_desktop || bannerData?.image;
};
